import {
  SecondaryButton,
  BUTTON_VARIANTS,
  IndeterminateProgressBar,
} from "@cdkglobal/radial";
import { useState } from "react";
import {
  RowDataObject,
  ReadRecordRequest,
  ShippingInfoRowsProps,
} from "../../../../../types";
import {
  TextButtonWrapper,
  StyledShippingInfoDialog,
  ShippingInfoInputsWrapper,
  ShippingInfoRowsContainer,
  StyledShippingInfoImportDialog,
  StyledShippingInfoSearchInput,
  StyledAddressActionItem,
  DefaultLabel,
  DefaultCardWrapper,
  DefaultAddressHeader,
} from "../../../../../utils/styled";
import { DEFAULT_TOAST } from "../../../constants";
import { AddressCardItem } from "./AddressCardItem";
import { getRowDataList } from "../../../helpers";
import { handlePOConfigurationGroupShippingInfoDefaultAddress } from "../../../evaluations/customEvaluations/evaluationHelpers/handlePOConfigurationGroupShippingInfoDefaultAddress";
import { useIntl } from "react-intl";
import { str } from "../inventory-buildings/utils/fieldUtils";

export default function ShippingInfoRows({
  inputValueObject = [],
  rows,
  propertyKey,
  properties,
  setupBaseRequestInfo,
  isReadOnly,
  isAddButtonDisabled,
  selectedTabSectionKey,
  rowDataReducerState,
  addRow,
  deleteRow,
  updateRow,
  renderInputType,
  canAddSingleRow,
  setRecordPageToast,
  dispatch,
}: Readonly<ShippingInfoRowsProps>): JSX.Element {
  const [activeRowIndex, setActiveRowIndex] = useState<number>(
    rows.length > 0 ? rows.length - 1 : 0,
  );
  const [isNewRow, setIsNewRow] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNewImportRow, setIsNewImportRow] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const intl = useIntl();
  const [originalEditedAddressRow, setOriginalEditedAddressRow] =
    useState<RowDataObject>({});
  const [previousDefaultRowIndex, setPreviousDefaultRowIndex] = useState<
    number | undefined
  >(undefined);
  const [previousDefaultRowData, setPreviousDefaultRowData] = useState<
    RowDataObject | undefined
  >(undefined);
  const [addressesList, setAddressesList] = useState<RowDataObject[]>([]);
  const [filteredAddressesList, setFilteredAddressesList] = useState<
    RowDataObject[]
  >([]);
  const [isAddressListFetching, setIsAddressListFetching] = useState(false);

  const [searchValue, setSearchValue] = useState("");

  const ADDRESS_NAME = "addr_nm";
  const IS_DEFAULT_ADDRESS = "is_default_address";

  const defaultAddress = rows
    .map((id, rowIndex) => ({ id, rowIndex }))
    .find(({ rowIndex }) => inputValueObject[rowIndex]?.[IS_DEFAULT_ADDRESS]);

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
    setRecordPageToast(DEFAULT_TOAST);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setIsImportDialogOpen(false);
  };

  const handleNewAddressOpen = () => {
    preserveDefaultAddressRowIndexAndRowData();
    const newRowIndex = rows.length;
    setIsNewRow(true);
    addRow();
    setActiveRowIndex(newRowIndex);
    handleDialogOpen();
  };

  const handleAdd = () => {
    if (canAddSingleRow(activeRowIndex)) {
      setPreviousDefaultRowIndex(undefined);
      setPreviousDefaultRowData(undefined);
      handleDialogClose();
    }
    handleResetIfCurrentRowIsNotSetToDefault(activeRowIndex);
  };

  const handleEdit = (rowIndex: number) => {
    preserveDefaultAddressRowIndexAndRowData();
    setIsNewRow(false);
    setActiveRowIndex(rowIndex);
    setOriginalEditedAddressRow(inputValueObject[rowIndex]);
    handleDialogOpen();
  };

  const handleAddressDialogCancel = () => {
    handleDialogClose();
    if (isNewRow) {
      // delete the new row
      deleteRow(activeRowIndex);
    } else {
      // reset the edited row to the original
      updateRow(activeRowIndex, originalEditedAddressRow);
    }
    resetToDefaultRowIndexAndRowData();
  };

  const handleSetAsDefault = (rowIndex: number) => {
    dispatch({
      type: "UPDATE_FIELD",
      payload: {
        propertyKey,
        propertySubKey: IS_DEFAULT_ADDRESS,
        rowIndex,
        newFieldValue: true,
        selectedTabSectionKey,
      },
    });

    handlePOConfigurationGroupShippingInfoDefaultAddress(
      propertyKey,
      IS_DEFAULT_ADDRESS,
      rowIndex,
      true,
      selectedTabSectionKey as string,
      rowDataReducerState,
      dispatch,
    );
  };

  const getDefaultAddressRow = (): number | undefined => {
    const index = inputValueObject.findIndex((row) => row[IS_DEFAULT_ADDRESS]);
    return index !== -1 ? index : undefined;
  };

  const preserveDefaultAddressRowIndexAndRowData = () => {
    const defaultIndex = getDefaultAddressRow();
    if (defaultIndex !== undefined) {
      setPreviousDefaultRowIndex(defaultIndex);
      setPreviousDefaultRowData({ ...inputValueObject[defaultIndex] });
    }
  };

  const handleResetIfCurrentRowIsNotSetToDefault = (activeRowIndex: number) => {
    if (!inputValueObject[activeRowIndex]?.[IS_DEFAULT_ADDRESS]) {
      resetToDefaultRowIndexAndRowData();
    }
  };

  const resetToDefaultRowIndexAndRowData = () => {
    if (previousDefaultRowIndex !== undefined && previousDefaultRowData) {
      // reset to the previous default row
      updateRow(previousDefaultRowIndex, previousDefaultRowData);
    }
  };

  const getVisibleInputs = (): JSX.Element[] => {
    return Object.entries(properties)
      .filter(([, item]) => !item.isHidden)
      .map(([propertySubKey, item]) => (
        <div key={propertySubKey}>
          {renderInputType({
            rowIndex: activeRowIndex,
            propertySubKey,
            itemProperty: item,
          })}
        </div>
      ));
  };

  const getVisibleInputsForRow = (rowIndex: number): JSX.Element[] => {
    return Object.entries(properties)
      .filter(([, item]) => !item.isHidden)
      .map(([propertySubKey, item]) => (
        <div key={propertySubKey}>
          {renderInputType({
            rowIndex,
            propertySubKey,
            itemProperty: item,
          })}
        </div>
      ));
  };

  const formatAddressString = (row: RowDataObject): string => {
    const keys = [
      "addr_line1",
      "addr_line2",
      "city_nm",
      "state_cd",
      "postal_cd",
      "county_nm",
    ];
    return keys
      .map((k) => str(row[k]))
      .filter(Boolean)
      .join(", ")
      .toUpperCase();
  };

  const handleFetchAddresses = async () => {
    setIsAddressListFetching(true);
    const request: ReadRecordRequest = {
      ...setupBaseRequestInfo,
      functionalUnitName: "PoAddress",
    };
    const fetchedAdressList = await getRowDataList(request);
    const existingAdresses = inputValueObject.map((row) => row[ADDRESS_NAME]);
    const removedExistingAdresses = fetchedAdressList.filter(
      (address) => !existingAdresses.includes(address[ADDRESS_NAME]),
    );
    const mappedAddresses = removedExistingAdresses.map((address) => ({
      ...address,
      [IS_DEFAULT_ADDRESS]: false,
    }));

    setAddressesList(mappedAddresses);
    setFilteredAddressesList(mappedAddresses);
    setIsAddressListFetching(false);

    return mappedAddresses;
  };

  const handleImportAddressOpen = () => {
    const newRowIndex = rows.length;
    setIsNewImportRow(true);
    addRow();
    setActiveRowIndex(newRowIndex);
    setIsImportDialogOpen(true);
    setSearchValue("");
    handleFetchAddresses();
  };

  const handleImport = (rowIndex: number) => {
    const rowToImport = filteredAddressesList[rowIndex];
    updateRow(activeRowIndex, rowToImport);
    setIsImportDialogOpen(true);
    handleDialogClose();
  };

  const handleImportAddressDialogClose = () => {
    if (isNewImportRow) {
      deleteRow(activeRowIndex);
    }
    handleDialogClose();
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    const filteredRecords = filterRecords(e.target.value, addressesList);
    setFilteredAddressesList(filteredRecords);
  };

  const filterRecords = (
    term: string,
    records: RowDataObject[],
  ): RowDataObject[] => {
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escapedTerm, "i");

    return records.filter((row: RowDataObject) =>
      Object.entries(row).some(([, value]) => regex.test(str(value))),
    );
  };

  return (
    <>
      {!isReadOnly && (
        <TextButtonWrapper>
          <SecondaryButton
            dataTestId={`add-${propertyKey}-row-button`}
            onClick={handleNewAddressOpen}
            text={intl.formatMessage({
              id: "button.addAddress",
              defaultMessage: "Add Address",
            })}
            size="small"
            isDisabled={isAddButtonDisabled}
          />
          <SecondaryButton
            dataTestId={`import-${propertyKey}-row-button`}
            onClick={handleImportAddressOpen}
            text={intl.formatMessage({
              id: "button.importAddress",
              defaultMessage: "Import Address",
            })}
            size="small"
            isDisabled={isAddButtonDisabled}
          />
        </TextButtonWrapper>
      )}
      {isDialogOpen && (
        <StyledShippingInfoDialog
          id="add-address-dialog"
          dataTestId="add-address-dialog"
          buttonsProps={[
            {
              id: "add-address-dialog-cancel-button",
              onClick: handleAddressDialogCancel,
              text: intl.formatMessage({
                id: "button.cancel",
                defaultMessage: "Cancel",
              }),
              variant: BUTTON_VARIANTS.TEXT,
            },
            {
              id: "add-address-dialog-add-button",
              onClick: handleAdd,
              text: isNewRow
                ? intl.formatMessage({
                    id: "button.add",
                    defaultMessage: "Add",
                  })
                : intl.formatMessage({
                    id: "button.update",
                    defaultMessage: "Update",
                  }),
              variant: BUTTON_VARIANTS.PRIMARY,
            },
          ]}
          isOpen={isDialogOpen}
          onClose={handleAddressDialogCancel}
          title={
            isNewRow
              ? intl.formatMessage({
                  id: "title.addAddress",
                  defaultMessage: "Add Address",
                })
              : intl.formatMessage({
                  id: "title.editAddress",
                  defaultMessage: "Edit Address",
                })
          }
        >
          <ShippingInfoInputsWrapper>
            {getVisibleInputs()}
          </ShippingInfoInputsWrapper>
        </StyledShippingInfoDialog>
      )}
      {isImportDialogOpen && (
        <StyledShippingInfoImportDialog
          id="import-address-dialog"
          dataTestId="import-address-dialog"
          isOpen={isImportDialogOpen}
          buttonsProps={[
            {
              id: "import-address-dialog-close",
              onClick: handleImportAddressDialogClose,
              text: intl.formatMessage({
                id: "button.close",
                defaultMessage: "Close",
              }),
              variant: BUTTON_VARIANTS.TEXT,
            },
          ]}
          onClose={handleImportAddressDialogClose}
          title={intl.formatMessage({
            id: "title.importAddressTitle",
            defaultMessage: "Import Address (One-click Import and Close)",
          })}
        >
          <ShippingInfoRowsContainer>
            {isAddressListFetching ? (
              <IndeterminateProgressBar
                dataTestId="indeterminate"
                label={intl.formatMessage({
                  id: "label.fetching",
                  defaultMessage: "Fetching...",
                })}
                loaderSize="large"
                trackWidth="500px"
                style={{ marginTop: "1rem" }}
              />
            ) : (
              <>
                <StyledShippingInfoSearchInput
                  id="search-input"
                  dataTestId="search-input"
                  name="search-input"
                  placeholder={intl.formatMessage({
                    id: "placeholder.searchAddress",
                    defaultMessage: "Search Address...",
                  })}
                  onChange={handleSearch}
                  isAlwaysExpanded
                  value={searchValue}
                  style={{ boxShadow: "none" }}
                />
                {filteredAddressesList.length === 0 && (
                  <div>
                    <p>
                      {intl.formatMessage({
                        id: "validation.noAddressesFound",
                        defaultMessage: "No addresses found...",
                      })}
                    </p>
                  </div>
                )}
                {filteredAddressesList.map((address, rowIndex) => (
                  <AddressCardItem
                    key={formatAddressString(address)}
                    rowIndex={rowIndex}
                    address={address}
                    fullAddress={formatAddressString(address)}
                    onImport={() => handleImport(rowIndex)}
                  />
                ))}
              </>
            )}
          </ShippingInfoRowsContainer>
        </StyledShippingInfoImportDialog>
      )}
      {defaultAddress && (
        <DefaultCardWrapper>
          <div key={defaultAddress.id}>
            <DefaultAddressHeader>
              <DefaultLabel>
                {intl.formatMessage({
                  id: "label.default",
                  defaultMessage: "Default",
                })}
              </DefaultLabel>
            </DefaultAddressHeader>

            <ShippingInfoInputsWrapper>
              {getVisibleInputsForRow(defaultAddress.rowIndex)}
            </ShippingInfoInputsWrapper>

            {!isReadOnly && (
              <StyledAddressActionItem
                dataTestId={`delete-${defaultAddress.rowIndex}-row-button`}
                text={intl.formatMessage({
                  id: "button.delete",
                  defaultMessage: "Delete",
                })}
                size="small"
                onClick={() => deleteRow(defaultAddress.rowIndex)}
                variant="text"
              />
            )}
          </div>
        </DefaultCardWrapper>
      )}
      <ShippingInfoRowsContainer>
        {rows
          .map((id, rowIndex) => ({ id, rowIndex }))
          .filter(
            ({ rowIndex }) => !inputValueObject[rowIndex]?.[IS_DEFAULT_ADDRESS],
          )
          .map(({ id, rowIndex }) => {
            const row = inputValueObject[rowIndex];

            return (
              <AddressCardItem
                key={id}
                rowIndex={rowIndex}
                address={row}
                fullAddress={formatAddressString(row)}
                isReadOnly={isReadOnly}
                onEdit={() => handleEdit(rowIndex)}
                onDelete={() => deleteRow(rowIndex)}
                onSetAsDefault={() => handleSetAsDefault(rowIndex)}
              />
            );
          })}
      </ShippingInfoRowsContainer>
    </>
  );
}
